import type { MessageResponseDto } from "../../types/types";
import { formatSportName } from "../utilMethods";
import { cleanDealId } from "./dealStatusMessage";
import type { ParsedDealStatus } from "./dealStatusMessage";

type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface DealStatusBannerContent {
  text: string;
  /** Internal deal id to open on click, or null when it is not usable. */
  routeDealId: string | null;
}

const KEY_BY_EVENT = {
  ACCEPTED: "system.dealAccepted",
  REJECTED: "system.dealRejected",
  CANCELLED: "system.dealWithdrawn",
} as const;

const getSenderName = (sender?: MessageResponseDto["sender"]): string =>
  `${sender?.firstName ?? ""} ${sender?.lastName ?? ""}`.trim();

/** Builds the banner sentence in the viewer's language. */
export const getDealStatusBannerContent = (
  parsed: ParsedDealStatus,
  sender: MessageResponseDto["sender"] | undefined,
  t: Translate,
): DealStatusBannerContent => {
  // LEGACY messages are English text that is shown as stored.
  if (parsed.kind === "legacy") {
    return { text: parsed.text, routeDealId: parsed.id };
  }

  const { event, id, dealId, dealType, sport, amount, currency } =
    parsed.payload;

  const typeText = dealType
    ? t(`dealTypes.${dealType}`, {
        defaultValue: dealType.replace(/_/g, " "),
      })
    : "";
  const sportText = sport
    ? t("system.forSport", { sport: formatSportName(sport) || sport })
    : "";
  const amountText =
    amount !== undefined && amount !== ""
      ? ` (${amount}${currency ? ` ${currency}` : ""})`
      : "";

  const summary = `${typeText}${sportText}${amountText}`.trim();

  return {
    text: t(KEY_BY_EVENT[event], {
      name: getSenderName(sender),
      summary,
      dealId,
    }).replace(/\s+/g, " ").trim(),
    routeDealId: cleanDealId(id),
  };
};
